import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  LeadBookingBookResult,
  LeadBookingBranding,
  LeadBookingPublicApiService,
  LeadBookingSlot,
} from '@services/api/lead-booking-public-api.service';

type DayGroup = { dayKey: string; label: string; slots: LeadBookingSlot[] };

/** Public page cannot load appointment details without a valid token; only distinguish missing vs expired/invalid. */
type LeadBookingLinkIssue = 'missing-token' | 'expired';

@Component({
  selector: 'app-lead-booking-public',
  templateUrl: './lead-booking-public.component.html',
  styleUrls: ['./lead-booking-public.component.scss'],
})
export class LeadBookingPublicComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  token = '';
  fromYmd = '';
  toYmd = '';
  loading = false;
  booking = false;
  bookingLinkIssue: LeadBookingLinkIssue | null = null;
  apiMessage: string | null = null;
  slots: LeadBookingSlot[] = [];
  dayGroups: DayGroup[] = [];
  selected: LeadBookingSlot | null = null;
  bookedSlot: LeadBookingSlot | null = null;
  success: LeadBookingBookResult | null = null;
  /** Theme from API (available even when status is false, e.g. expired link). */
  branding: LeadBookingBranding | null = null;

  rangeLabel = '';

  /** CSS variables scoped to this page when branding is present. */
  get brandingStyle(): Record<string, string> {
    const b = this.branding;
    if (!b) {
      return {};
    }
    const style: Record<string, string> = {};
    if (b.primary_color) {
      style['--primary'] = b.primary_color;
    }
    if (b.secondary_color) {
      style['--secondary'] = b.secondary_color;
    }
    if (b.text_color) {
      style['--lb-on-primary'] = b.text_color;
    }
    return style;
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly api: LeadBookingPublicApiService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.token = (params.get('token') || '').trim();
      this.success = null;
      this.bookedSlot = null;
      this.selected = null;
      this.apiMessage = null;
      this.bookingLinkIssue = null;
      this.branding = null;
      if (!this.token) {
        this.bookingLinkIssue = 'missing-token';
        this.slots = [];
        this.dayGroups = [];
        return;
      }
      this.initWeekRange();
      this.loadAvailability();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  prevWeek(): void {
    if (!this.token || this.loading) {
      return;
    }
    this.shiftRange(-7);
    this.loadAvailability();
  }

  nextWeek(): void {
    if (!this.token || this.loading) {
      return;
    }
    this.shiftRange(7);
    this.loadAvailability();
  }

  selectSlot(slot: LeadBookingSlot): void {
    this.selected = slot;
    this.apiMessage = null;
  }

  confirmBooking(): void {
    if (!this.token || !this.selected || this.booking) {
      return;
    }
    this.booking = true;
    this.apiMessage = null;
    this.api
      .book({
        token: this.token,
        slot_start: this.selected.slot_start,
        slot_end: this.selected.slot_end,
        timezone: this.selected.timezone,
      })
      .subscribe({
        next: res => {
          this.booking = false;
          if (res.status && res.data) {
            this.bookedSlot = this.selected;
            this.success = res.data;
            this.selected = null;
          } else {
            this.apiMessage = res.message || 'Could not complete booking.';
          }
        },
        error: err => {
          this.booking = false;
          if (err instanceof HttpErrorResponse && err.status === 401) {
            this.bookingLinkIssue = 'expired';
            return;
          }
          this.apiMessage = this.messageFromHttpError(err);
        },
      });
  }

  trackBySlot(_: number, slot: LeadBookingSlot): string {
    return slot.slot_start;
  }

  trackByDay(_: number, g: DayGroup): string {
    return g.dayKey;
  }

  private initWeekRange(): void {
    const start = moment().startOf('day');
    this.fromYmd = start.format('YYYY-MM-DD');
    this.toYmd = start.clone().add(6, 'days').format('YYYY-MM-DD');
    this.updateRangeLabel();
  }

  private shiftRange(days: number): void {
    const start = moment(this.fromYmd, 'YYYY-MM-DD').add(days, 'days');
    this.fromYmd = start.format('YYYY-MM-DD');
    this.toYmd = start.clone().add(6, 'days').format('YYYY-MM-DD');
    this.updateRangeLabel();
  }

  private updateRangeLabel(): void {
    const a = moment(this.fromYmd, 'YYYY-MM-DD');
    const b = moment(this.toYmd, 'YYYY-MM-DD');
    if (a.isSame(b, 'month')) {
      this.rangeLabel = `${a.format('D MMM')} – ${b.format('D MMM YYYY')}`;
    } else {
      this.rangeLabel = `${a.format('D MMM')} – ${b.format('D MMM YYYY')}`;
    }
  }

  private loadAvailability(): void {
    if (!this.token) {
      return;
    }
    this.loading = true;
    this.bookingLinkIssue = null;
    this.apiMessage = null;
    this.api.getAvailability(this.token, this.fromYmd, this.toYmd).subscribe({
      next: res => {
        this.loading = false;
        const data = res.data;
        if (data?.branding) {
          this.branding = data.branding;
        }
        if (res.status) {
          const rawSlots = data?.slots ?? [];
          this.slots = [...rawSlots].sort((x, y) => x.slot_start.localeCompare(y.slot_start));
          this.dayGroups = this.groupByDay(this.slots);
        } else {
          this.slots = [];
          this.dayGroups = [];
          const msg = res.message || 'No availability for this range.';
          if (this.messageSuggestsInvalidToken(msg)) {
            this.bookingLinkIssue = 'expired';
          } else {
            this.apiMessage = msg;
          }
        }
      },
      error: err => {
        this.loading = false;
        this.slots = [];
        this.dayGroups = [];
        this.applyBrandingFromHttpError(err);
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.bookingLinkIssue = 'expired';
        } else {
          this.apiMessage = this.messageFromHttpError(err);
        }
      },
    });
  }

  private groupByDay(slots: LeadBookingSlot[]): DayGroup[] {
    const map = new Map<string, LeadBookingSlot[]>();
    for (const slot of slots) {
      const key = this.dayKeyInZone(slot.slot_start, slot.timezone);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(slot);
    }
    const keys = [...map.keys()].sort();

    return keys.map(dayKey => {
      const daySlots = map.get(dayKey)!;
      const first = daySlots[0];
      const label = new Intl.DateTimeFormat(undefined, {
        timeZone: first.timezone,
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date(first.slot_start));
      return { dayKey, label, slots: daySlots };
    });
  }

  private dayKeyInZone(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(iso));
  }

  formatBookedWhen(slot: LeadBookingSlot): string {
    const day = new Intl.DateTimeFormat(undefined, {
      timeZone: slot.timezone,
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(slot.slot_start));
    return `${day} · ${this.formatSlotRange(slot)}`;
  }

  formatSlotRange(slot: LeadBookingSlot): string {
    const opts: Intl.DateTimeFormatOptions = {
      timeZone: slot.timezone,
      hour: 'numeric',
      minute: '2-digit',
    };
    const start = new Intl.DateTimeFormat(undefined, opts).format(new Date(slot.slot_start));
    const end = new Intl.DateTimeFormat(undefined, opts).format(new Date(slot.slot_end));
    return `${start} – ${end}`;
  }

  private applyBrandingFromHttpError(err: unknown): void {
    if (!(err instanceof HttpErrorResponse) || err.error == null || typeof err.error !== 'object') {
      return;
    }
    const body = err.error as { data?: { branding?: LeadBookingBranding | null } };
    if (body.data?.branding) {
      this.branding = body.data.branding;
    }
  }

  private messageSuggestsInvalidToken(message: string): boolean {
    const m = message.toLowerCase();
    if (!m.includes('token')) {
      return false;
    }
    return /\b(invalid|expired|unauthori[sz]ed|denied)\b/.test(m);
  }

  private messageFromHttpError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string') {
        return (body as { message: string }).message;
      }
      return err.message || 'Something went wrong.';
    }
    if (typeof err === 'string') {
      return err;
    }

    return 'Something went wrong.';
  }
}
